/**
 * Несколько учётных записей в одном браузере (мультиаккаунт, docs/multi-account/PLAN.md §7): гейт интерфейса и то,
 * чем приложение дополняет связку `auth.accounts` для `@srm/ui` (хуки меню и страницы входа): тексты отказов и адреса.
 */
import { addAccountSearch, describeAccountsFailure } from '@srm/core/auth';
import { normalizeBasePath } from '@srm/core/routing';
import type { IAccountsPort } from '@srm/ui/shell/AccountSwitch';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { BASE_PATH } from '@/configuration';
import { ConfigService } from '@/configuration/services/configService';
import { RouteNames } from '@/router/router.types';
import { auth } from './index';

/**
 * Мультиаккаунт в интерфейсе: связка включена (флаг сервера, не вкладка входа «от имени», есть Web Locks —
 * `auth.accounts.enabled()`) и стенд не PROD — второй замок на случай ошибки в серверном конфиге.
 */
export const isMultiAccountEnabled = (): boolean =>
    ConfigService.get().environment !== 'PROD' && auth.accounts.enabled();

/** Мультиаккаунт для компонента: перерисовка по настройкам входа, предел — оттуда же (вместе с активной). */
export function useMultiAccount(): { enabled: boolean; maxAccounts: number } {
    const settings = useTypedSelector((state) => state.auth.signInSettings?.multiAccount);
    return { enabled: !!settings?.enabled && isMultiAccountEnabled(), maxAccounts: settings?.maxAccounts ?? 0 };
}

/** Адрес с basename приложения — для полного перехода (`location.assign`), а не навигации роутера. */
export const appUrl = (path: string): string =>
    `${normalizeBasePath(BASE_PATH)}${path.startsWith('/') ? path : `/${path}`}`;

/** Связка, тексты отказов и адрес страницы «Добавить учётную запись» с возвратом на `returnUrl` (путь от basename). */
export const accountsUi = {
    accounts: auth.accounts satisfies IAccountsPort,
    describeFailure: (error: unknown, maxAccounts?: number) => describeAccountsFailure(error, { maxAccounts }),
    addAccountUrl: (returnUrl?: string) => appUrl(`${RouteNames.LOGIN}?${addAccountSearch(returnUrl)}`),
};
